import { z } from "zod";
import { CodeSchema } from "./Codes";
import { ProjectStatusSchema } from "./ProjectStatus";

export const SubdivisionDrawTotalSchema = z.object({
  // Project
  projectId: z.string(),
  projectName: z.string(),
  projectCode: z.string(),
  projectStatus: ProjectStatusSchema,
  propertyType: z.string(),
  squareFootage: z.number().nullable(),
  appraisalDate: z.coerce.date().nullable(),
  maturityDate: z.coerce.date().nullable(),
  completionPosted: z.coerce.date().nullable(),
  extensions: z.number().nullable(),
  retailValue: z.number().nullable(),
  salesPrice: z.number().nullable(),
  releaseAmount: z.number().nullable(),
  releaseDate: z.coerce.date().nullable(),
  // Draw
  dateCreated: z.coerce.date().nullable(),
  drawNumber: z.number(),
  totalCredit: z.number(),
  totalEquity: z.number(),
  totalAdjustedBudget: z.number(),
  totalThisDraw: z.number(),
  totalDrawnToDate: z.number(),
  totalFundsAvailable: z.number(),
  totalFundsRequired: z.number(),
  totalBudgetDrawn: z.number(),
  totalSiteComplete: z.number(),
  totalHardCosts: z.number().nullable().default(0),
  totalSoftCosts: z.number().nullable().default(0),
  totalLand: z.number(),
  totalInterest: z.number(),
  totalInspection: z.number(),
  totalFees: z.number(),
  totalCreditCommitment: z.number(),
  principalBalance: z.number().nullable(),

  initialBudget: z.number(),
  budgetAmount: z.number(),
  cumulativeAdjustment: z.number(),
  priorDrawnToDate: z.number(),
  totalThisLoan: z.number(),
  totalThisEquity: z.number(),
  netThisDraw: z.number(),
  priorEquityToDate: z.number(),
  projectedOver: z.number(),
  loanToDate: z.number(),
  netThisDrawEquity: z.number(),
  equityToDate: z.number(),
  variance: z.number(),
  totalRetainagePriorDraw: z.number(),
  totalReleasedPriorDraw: z.number(),
  totalRetainage: z.number(),
  totalReleased: z.number(),
  retainageBalance: z.number(),
});

export const SubdivisionPaydownSchema = z.object({
  ordinal: z.number(),
  dateStamp: z.coerce.date(),
  principalBalance: z.number(),
  amount: z.number(),
});

export const SubdivisionPaydownListSchema = z.array(SubdivisionPaydownSchema);

export const SubdivisionSchema = z.object({
  subdivisionId: z.string(),
  subdivisionName: z.string(),
  maturityDate: z.coerce.date().nullable(),
  specLimit: z.number(),
  modelLimit: z.number(),
  preSoldLimit: z.number(),
  creditLine: z.number(),
  paidLand: z.number(),
  netCost: z.number(),
  subdivisionNotes: z.string().default(""),
  userGroup: CodeSchema,
  startDate: z.coerce.date(),
  totalUnits: z.number(),
  totalPending: z.number(),
  totalActive: z.number(),
  totalSold: z.number(),
  totalPercentComplete: z.number(),
  totalCost: z.number(),
  totalEquity: z.number(),
  totalBudget: z.number(),
  totalDrawnToDate: z.number(),
  totalFundsAvailable: z.number(),
  paydowns: z.array(SubdivisionPaydownSchema),
  drawTotals: z.array(SubdivisionDrawTotalSchema),
  totalPrincipalBalance: z.number(),
  subdivisionColumns: z.number().nullable(),
  subdivisionColumnOrder: z.array(z.number()),
});

export const SubdivisionListSchema = z.array(SubdivisionSchema);

export type Subdivision = z.infer<typeof SubdivisionSchema>;
export type SubdivisionDrawTotal = z.infer<typeof SubdivisionDrawTotalSchema>;
export type SubdivisionPaydown = z.infer<typeof SubdivisionPaydownSchema>;
